/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports, LIST_TAG_ID } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { PROFILE_API_ENDPOINTS as cfg } from './config';

// Enhance the generated API with custom cache invalidation logic
const enhancedProfileApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Profile', 'ProfileTenants'],
  endpoints: {
    [cfg.getProfile.generated]: {
      providesTags: ['Profile'],
    },
    [cfg.updateProfile.generated]: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            generatedApi.util.updateQueryData(cfg.getProfile.generated, undefined, () => data),
          );
        } catch (error) {
          console.error('Update profile error:', error);
        }
      },
    },
    // Reference list of the tenants the current user belongs to. No mutation in
    // this package invalidates it, so the `id: 'LIST'` tag is for consistency with
    // the rest of the data layer and to allow a future refresh to target it.
    [cfg.listProfileTenants.generated]: {
      providesTags: [{ type: 'ProfileTenants', id: LIST_TAG_ID }],
    },
    [cfg.verifyTelegram.generated]: {
      invalidatesTags: ['Profile'],
    },
  },
});

export const profileApi: typeof enhancedProfileApi = enhancedProfileApi;

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof profileApi> = createHookExports(
  cfg,
  profileApi,
);

// Re-export endpoints object with cleaner names for direct usage
const endpointExports = createEndpointExports(cfg, profileApi);

export const profileApiEndpoints: typeof endpointExports = endpointExports;
